import { ArrowRight, Network, Plus, RefreshCw, ShieldAlert, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client.js";
import type { ThreatModelWorkspace } from "../../api/types.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { shortHash } from "../../lib/format.js";
import {
  draftFromWorkspace,
  elementValidationError,
  type ThreatModelDraft,
  type ThreatModelDraftElement,
  threatModelPayload,
  workspaceRevisionKey,
} from "./draft.js";
import { GovernedGraphFactEditor } from "./GovernedGraphFactEditor.js";
import { ReproductionPanel } from "./ReproductionPanel.js";
import { ThreatModelOmissionQueue } from "./ThreatModelOmissionQueue.js";
import { ThreatModelSnapshotGraph } from "./ThreatModelSnapshotGraph.js";
import { snapshotHeadline } from "./threat-model-projection.js";

const EMPTY_ELEMENT: Omit<ThreatModelDraftElement, "id"> = {
  kind: "asset",
  title: "",
  description: "",
  nodeIds: [],
  edgeIds: [],
};

const THREAT_MODEL_EXAMPLES = [
  {
    title: "Public request → interpreter boundary",
    asset: "Application process",
    proof: "Cited source line plus reviewed data-flow edge",
    decision: "Fix the sink or record why the path is not reachable",
  },
  {
    title: "Service token → cloud control plane",
    asset: "Privileged infrastructure action",
    proof: "Secret evidence fingerprint plus owner-approved boundary",
    decision: "Rotate, scope, and prove least privilege",
  },
  {
    title: "Package install → runtime workload",
    asset: "Build and deployment path",
    proof: "SBOM component linked to affected file and scan snapshot",
    decision: "Patch or quarantine the dependency path",
  },
  {
    title: "Classical crypto → long-lived trust",
    asset: "Future confidentiality and signatures",
    proof: "CBOM use, protocol role, and migration dependency",
    decision: "Plan hybrid/PQC migration after retention is known",
  },
];

function toggle(values: readonly string[], value: string): string[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function graphProofLabel(input: {
  evidenceCount: number;
  approval?: { approvedBy: string; approvalSha256: string } | null | undefined;
}): string {
  if (input.approval) {
    return `Human-reviewed graph fact · ${graphOwnerLabel(input.approval.approvedBy)}`;
  }
  return `${input.evidenceCount} replay-matched record(s)`;
}

function graphOwnerLabel(owner: string): string {
  return owner.includes(".demo@") ? "Risk governance" : owner;
}

function ThreatModelExamples() {
  return (
    <section className="threat-model-examples" aria-labelledby="threat-model-examples-heading">
      <div>
        <span className="page-kicker">Example paths this feature proves</span>
        <h2 id="threat-model-examples-heading">
          A threat model is only useful when every edge can defend itself.
        </h2>
        <p>
          These demo examples show the shape Provenance is designed to prove: entry, boundary, asset,
          evidence, and the decision that follows.
        </p>
      </div>
      <div className="threat-model-example-grid">
        {THREAT_MODEL_EXAMPLES.map((example, index) => (
          <article key={example.title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <h3>{example.title}</h3>
            <dl>
              <div>
                <dt>Asset at risk</dt>
                <dd>{example.asset}</dd>
              </div>
              <div>
                <dt>Required proof</dt>
                <dd>{example.proof}</dd>
              </div>
              <div>
                <dt>Reviewer decision</dt>
                <dd>{example.decision}</dd>
              </div>
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
}

export function ThreatModelGraphFacts({ workspace }: { workspace: ThreatModelWorkspace }) {
  const { graph } = workspace.projection;
  const nodeNames = new Map(graph.nodes.map((node) => [node.id, node.name]));
  const primaryPath = workspace.projection.attackPaths.items[0];
  return (
    <div className="threat-model-facts">
      {primaryPath ? (
        <article className="threat-path-visual" aria-label="Primary governed attack path">
          <div className="threat-path-visual__header">
            <span className="page-kicker">Validated threat path</span>
            <h2>
              {nodeNames.get(primaryPath.fromNodeId) ?? primaryPath.fromNodeId} can reach{" "}
              {nodeNames.get(primaryPath.crownJewelNodeId) ?? primaryPath.crownJewelNodeId}
            </h2>
            <p>
              This is the scary part: the path is explicit, bounded, and custody-hashed. It is not a
              guessed graph drawn to look impressive.
            </p>
          </div>
          <ol className="threat-path-visual__chain">
            {primaryPath.nodeIds.map((nodeId, index) => (
              <li key={nodeId}>
                <span>{index + 1}</span>
                <strong>{nodeNames.get(nodeId) ?? nodeId}</strong>
                <small>
                  {index === 0
                    ? "Ingress"
                    : nodeId === primaryPath.crownJewelNodeId
                      ? `${primaryPath.sensitivity} ${primaryPath.classification}`
                      : "Control boundary"}
                </small>
                {index < primaryPath.nodeIds.length - 1 ? (
                  <ArrowRight aria-hidden="true" size={18} />
                ) : null}
              </li>
            ))}
          </ol>
          <div className="threat-path-visual__proof">
            <span>{primaryPath.edgeIds.length} validated edge(s)</span>
            <span>{primaryPath.basis.replaceAll("_", " ")}</span>
            <span title={primaryPath.canonicalSha256}>custody-hashed proof</span>
          </div>
        </article>
      ) : null}
      <details className="governance-detail threat-model-supporting-detail" open>
        <summary>Evidence dossier: what is proved, what stays out, and where custody lives</summary>
        <div className="proof-dossier-summary">
          <article>
            <span>Observed assets</span>
            <strong>{graph.nodes.length}</strong>
            <p>Nodes admitted from replay-backed or human-reviewed graph facts.</p>
          </article>
          <article>
            <span>Validated edges</span>
            <strong>{graph.edges.length}</strong>
            <p>Relationships strong enough to support an attack path.</p>
          </article>
          <article>
            <span>Omitted evidence</span>
            <strong>{workspace.projection.omissions.length}</strong>
            <p>Records kept visible without inflating the threat graph.</p>
          </article>
        </div>
        <div className="threat-model-supporting-grid">
          <article className="panel-card proof-dossier-card proof-dossier-card--primary">
            <h2>
              <Network aria-hidden="true" size={20} /> Evidence-observed nodes
            </h2>
            <p className="muted-copy">
              The minimum explainer table users need: what asset or entry point exists, why it
              was admitted, and what the projection depends on.
            </p>
            <div className="table-wrap">
              <table className="data-table" aria-label="Evidence-observed threat-model nodes">
                <thead>
                  <tr>
                    <th scope="col">Node</th>
                    <th scope="col">Kind</th>
                    <th scope="col">Projection basis</th>
                    <th scope="col">Admission note</th>
                  </tr>
                </thead>
                <tbody>
                  {graph.nodes.map((node) => (
                    <tr key={node.id}>
                      <th scope="row">{node.name}</th>
                      <td>{node.kind}</td>
                      <td>
                        {graphProofLabel({
                          evidenceCount: node.evidence.length,
                          approval: node.approval,
                        })}
                      </td>
                      <td>
                        {node.approval
                          ? "Human approval recorded"
                          : (node.attributes?.projectionBasis ?? "Replay-backed governed fact")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <details className="panel-card proof-dossier-card">
            <summary>Validated trust and data edges</summary>
            {graph.edges.length ? (
              <div className="table-wrap">
                <table className="data-table" aria-label="Validated threat-model edges">
                  <thead>
                    <tr>
                      <th scope="col">Edge</th>
                      <th scope="col">From</th>
                      <th scope="col">To</th>
                      <th scope="col">Kind</th>
                      <th scope="col">Proof basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {graph.edges.map((edge) => (
                      <tr key={edge.id}>
                        <th scope="row">{shortHash(edge.id, 18)}</th>
                        <td>{nodeNames.get(edge.fromNodeId) ?? edge.fromNodeId}</td>
                        <td>{nodeNames.get(edge.toNodeId) ?? edge.toNodeId}</td>
                        <td>{edge.kind}</td>
                        <td>
                          {graphProofLabel({
                            evidenceCount: edge.evidence.length,
                            approval: edge.approval,
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="unavailable-value">
                Not measured — no governed replay-backed trust edge is present in this frozen graph.
              </p>
            )}
          </details>

          <details className="panel-card proof-dossier-card">
            <summary>Crown jewels and required invariants</summary>
            {graph.crownJewels.length ? (
              <div className="table-wrap">
                <table className="data-table" aria-label="Evidence-bound crown jewels">
                  <thead>
                    <tr>
                      <th scope="col">Classification</th>
                      <th scope="col">Node</th>
                      <th scope="col">Owner</th>
                      <th scope="col">Sensitivity</th>
                      <th scope="col">Required invariants</th>
                    </tr>
                  </thead>
                  <tbody>
                    {graph.crownJewels.map((jewel) => (
                      <tr key={jewel.id}>
                        <th scope="row">{jewel.classification}</th>
                        <td>{jewel.nodeId}</td>
                        <td>{graphOwnerLabel(jewel.owner)}</td>
                        <td>{jewel.sensitivity}</td>
                        <td>
                          {jewel.requiredInvariants.join(" · ")}
                          {jewel.approval
                            ? ` · approved ${shortHash(jewel.approval.approvalSha256, 12)}`
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="unavailable-value">
                Unknown — no evidence-bound crown-jewel fact is present. Provenance does not infer one from
                a finding title or repository name.
              </p>
            )}
          </details>

          <details className="panel-card proof-dossier-card">
            <summary>Governed ingress-to-crown-jewel paths</summary>
            <p>{workspace.projection.attackPaths.limitation}</p>
            {workspace.projection.attackPaths.status === "truncated" ? (
              <p className="unavailable-value">
                Partial result — the bounded search limit was reached. This table is not complete.
              </p>
            ) : null}
            {workspace.projection.attackPaths.items.length ? (
              <div className="table-wrap">
                <table className="data-table" aria-label="Governed attack paths">
                  <thead>
                    <tr>
                      <th scope="col">Review order</th>
                      <th scope="col">Ingress → crown jewel</th>
                      <th scope="col">Validated edge chain</th>
                      <th scope="col">Basis</th>
                      <th scope="col">Required invariants</th>
                      <th scope="col">Custody hash</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspace.projection.attackPaths.items.map((path, index) => (
                      <tr key={path.id}>
                        <th scope="row">
                          {index + 1} · {path.sensitivity} · {path.reviewOrder.hopCount} hop(s)
                        </th>
                        <td>
                          {nodeNames.get(path.fromNodeId) ?? path.fromNodeId} →{" "}
                          {nodeNames.get(path.crownJewelNodeId) ?? path.crownJewelNodeId} ·{" "}
                          {path.classification}
                        </td>
                        <td>{path.edgeIds.length} validated step(s)</td>
                        <td>
                          {path.basis.replaceAll("_", " ")} · {path.replayBackedEdges} replay /{" "}
                          {path.humanApprovedEdges} approved edge(s)
                        </td>
                        <td>{path.requiredInvariants.join(" · ")}</td>
                        <td>
                          <code>{shortHash(path.canonicalSha256, 14)}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="unavailable-value">
                Unavailable — no complete, bounded path reaches a crown jewel from an explicitly
                governed ingress. Provenance does not infer missing links.
              </p>
            )}
          </details>

          <details className="panel-card proof-dossier-card">
            <summary>Governed graph-fact custody</summary>
            {workspace.factRevisions.length ? (
              <div className="table-wrap">
                <table className="data-table" aria-label="Governed graph-fact revision history">
                  <thead>
                    <tr>
                      <th scope="col">Fact</th>
                      <th scope="col">Decision</th>
                      <th scope="col">Owner / approver</th>
                      <th scope="col">Source</th>
                      <th scope="col">Revision hash</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...workspace.factRevisions].reverse().map((revision) => (
                      <tr key={revision.id}>
                        <th scope="row">
                          {revision.factType} · {shortHash(revision.subjectId, 18)} · r
                          {revision.revision}
                        </th>
                        <td>{revision.decision}</td>
                        <td>
                          {revision.ownerId} / {revision.approvedBy}
                        </td>
                        <td>
                          {shortHash(revision.sourceRef, 18)} · {revision.rationale}
                        </td>
                        <td>
                          <code>{shortHash(revision.canonicalSha256, 14)}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No human-approved graph facts have been recorded for this snapshot.</p>
            )}
          </details>
        </div>
      </details>
    </div>
  );
}

export function CurrentThreatModel({ workspace }: { workspace: ThreatModelWorkspace }) {
  const latest = workspace.revisions.at(-1);
  if (!latest) {
    return (
      <article className="panel-card">
        <h2>Accepted model</h2>
        <p>No reviewed overlay has been appended. Observed graph facts remain separate.</p>
      </article>
    );
  }
  return (
    <article className="panel-card">
      <h2>
        Accepted model · revision {latest.revision}: {latest.title}
      </h2>
      <p>
        Parent{" "}
        <code title={latest.parentSha256 ?? "initial"}>
          {latest.parentSha256 ? shortHash(latest.parentSha256, 14) : "initial"}
        </code>{" "}
        · graph <code title={latest.graphSha256}>{shortHash(latest.graphSha256, 14)}</code>
      </p>
      <div className="table-wrap">
        <table className="data-table" aria-label="Accepted threat-model elements">
          <thead>
            <tr>
              <th scope="col">Element</th>
              <th scope="col">Kind</th>
              <th scope="col">Description</th>
              <th scope="col">Graph bindings</th>
              <th scope="col">Proof basis</th>
            </tr>
          </thead>
          <tbody>
            {latest.elements.map((element) => (
              <tr key={element.id}>
                <th scope="row">{element.title}</th>
                <td>{element.kind}</td>
                <td>{element.description}</td>
                <td>
                  {element.nodeIds.length} node(s) · {element.edgeIds.length} edge(s)
                </td>
                <td>
                  {element.evidence.length > 0
                    ? `${element.evidence.length} replay-matched record(s)`
                    : element.nodeIds.length + element.edgeIds.length > 0
                      ? "Reviewed graph binding"
                      : "Narrative element"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3>Unresolved assumptions</h3>
      {latest.assumptions.length ? (
        <ul>
          {latest.assumptions.map((assumption) => (
            <li key={assumption}>{assumption}</li>
          ))}
        </ul>
      ) : (
        <p>None recorded.</p>
      )}
      <p>
        Accepted by {latest.editorId} at {new Date(latest.createdAt).toLocaleString()} ·{" "}
        <code title={latest.canonicalSha256}>{shortHash(latest.canonicalSha256, 14)}</code>
      </p>
    </article>
  );
}

export function ThreatModelsPage({ canEdit }: { canEdit: boolean }) {
  const scanLoader = useCallback(
    (signal: AbortSignal) => api.getScans({ page: 1, pageSize: 500 }, signal),
    [],
  );
  const scans = useApiResource(scanLoader);
  const eligibleScans = useMemo(
    () => scans.data?.items.filter((scan) => scan.snapshotId) ?? [],
    [scans.data],
  );
  const [snapshotId, setSnapshotId] = useState("");
  useEffect(() => {
    if (!snapshotId && eligibleScans[0]?.snapshotId) setSnapshotId(eligibleScans[0].snapshotId);
  }, [eligibleScans, snapshotId]);
  const workspaceLoader = useCallback(
    (signal: AbortSignal) =>
      snapshotId ? api.getThreatModels(snapshotId, signal) : Promise.resolve(null),
    [snapshotId],
  );
  const workspace = useApiResource(workspaceLoader);
  const [draftState, setDraftState] = useState<{ key: string; draft: ThreatModelDraft } | null>(
    null,
  );
  const [newElement, setNewElement] = useState<Omit<ThreatModelDraftElement, "id">>(EMPTY_ELEMENT);
  const [changeReason, setChangeReason] = useState("");
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);

  const workspaceKey = workspace.data ? workspaceRevisionKey(workspace.data) : "none";
  const draft = workspace.data
    ? draftState?.key === workspaceKey
      ? draftState.draft
      : draftFromWorkspace(workspace.data)
    : null;
  const setDraft = (next: ThreatModelDraft) => setDraftState({ key: workspaceKey, draft: next });
  const pendingElement: ThreatModelDraftElement = { id: "pending-element", ...newElement };
  const pendingError = workspace.data
    ? elementValidationError(pendingElement, workspace.data)
    : "No workspace is loaded.";
  const validationWorkspace = workspace.data;
  const draftErrors =
    draft && validationWorkspace
      ? draft.elements
          .map((element) => elementValidationError(element, validationWorkspace))
          .filter(Boolean)
      : [];

  function addElement() {
    if (!draft || pendingError) return;
    setDraft({
      ...draft,
      elements: [...draft.elements, { ...pendingElement, id: `element-${crypto.randomUUID()}` }],
    });
    setNewElement(EMPTY_ELEMENT);
  }

  async function save() {
    if (!snapshotId || !draft || !workspace.data || draftErrors.length > 0) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.createThreatModel(threatModelPayload(snapshotId, draft, changeReason));
      setChangeReason("");
      setDraftState(null);
      workspace.refresh();
    } catch (error) {
      setSaveError(error instanceof Error ? error : new Error("Threat model save failed"));
    } finally {
      setSaving(false);
    }
  }

  if (scans.isLoading && !scans.data) return <LoadingTable columns={3} rows={5} />;
  if (scans.error && !scans.data)
    return <InlineError error={scans.error} onRetry={scans.refresh} />;

  return (
    <section className="page-stack threat-model-page">
      <header className="page-heading">
        <div>
          <span className="page-kicker">Evidence-bound architecture</span>
          <h1>Threat models</h1>
          <p>
            See the relationships the evidence supports—and the ones this snapshot cannot yet prove.
            Nothing is guessed into an attack path.
          </p>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Refresh threat-model workspace"
          title="Refresh"
          disabled={workspace.isRefreshing || scans.isRefreshing}
          onClick={() => {
            scans.refresh();
            workspace.refresh();
          }}
        >
          <RefreshCw aria-hidden="true" size={17} />
        </button>
      </header>

      <label className="form-field threat-model-scan-picker">
        <span>Frozen scan snapshot</span>
        <select value={snapshotId} onChange={(event) => setSnapshotId(event.target.value)}>
          {eligibleScans.map((scan) => (
            <option key={scan.id} value={scan.snapshotId ?? ""}>
              {scan.repositoryName} · frozen snapshot · {new Date(scan.startedAt).toLocaleString()}
            </option>
          ))}
        </select>
      </label>

      {!snapshotId ? (
        <EmptyState
          title="No frozen scan is available"
          description="Complete a static scan before creating an evidence-bound threat model."
        />
      ) : workspace.isLoading && !workspace.data ? (
        <LoadingTable columns={3} rows={5} />
      ) : workspace.error && !workspace.data ? (
        <InlineError error={workspace.error} onRetry={workspace.refresh} />
      ) : workspace.data && draft ? (
        <>
          <section
            className="threat-model-decision-hero"
            aria-label="Threat-model evidence summary"
          >
            <div>
              <span className="page-kicker">This frozen snapshot</span>
              <h2>What can we say about this architecture?</h2>
              <p>
                {snapshotHeadline({
                  nodeCount: workspace.data.projection.graph.nodes.length,
                  edgeCount: workspace.data.projection.graph.edges.length,
                  pathCount: workspace.data.projection.attackPaths.items.length,
                  omittedCount: workspace.data.projection.counts.omitted,
                })}
              </p>
            </div>
            <dl className="threat-model-hero-metrics">
              <div data-metric="nodes">
                <dt>Observed assets</dt>
                <dd>{workspace.data.projection.graph.nodes.length}</dd>
              </div>
              <div data-metric="edges">
                <dt>Validated edges</dt>
                <dd>{workspace.data.projection.graph.edges.length}</dd>
              </div>
              <div data-metric="omitted">
                <dt>Omitted</dt>
                <dd>{workspace.data.projection.counts.omitted}</dd>
              </div>
            </dl>
          </section>

          <ThreatModelSnapshotGraph workspace={workspace.data} />

          {workspace.data.projection.graph.edges.length === 0 ? (
            <div className="callout warning-callout">
              <ShieldAlert aria-hidden="true" size={20} />
              <div>
                <strong>No validated trust edge is claimed.</strong>
                <p>
                  We will not draw an attack path this snapshot cannot support. Add a reviewed graph
                  fact only when the evidence exists.
                </p>
              </div>
            </div>
          ) : null}

          <ThreatModelOmissionQueue
            omissions={workspace.data.projection.omissions}
            scanId={eligibleScans.find((scan) => scan.snapshotId === snapshotId)?.id}
          />

          {workspace.data.projection.attackPaths.items.length > 0 ||
          workspace.data.projection.graph.nodes.length > 0 ? (
            <ThreatModelGraphFacts workspace={workspace.data} />
          ) : null}

          <details className="governance-detail">
            <summary>Example of a proved path, not this scan</summary>
            <ThreatModelExamples />
          </details>

          <details className="governance-detail">
            <summary>Show accepted model and unresolved assumptions</summary>
            <CurrentThreatModel workspace={workspace.data} />
          </details>
          <details
            className="governance-detail"
            open={workspace.data.projection.graph.edges.length === 0}
          >
            <summary>Add a reviewed graph fact or reproduction</summary>
            {canEdit ? (
              <GovernedGraphFactEditor workspace={workspace.data} onSaved={workspace.refresh} />
            ) : null}
            <ReproductionPanel
              workspace={workspace.data}
              canRun={canEdit}
              onSaved={workspace.refresh}
            />

            <article className="panel-card" aria-labelledby="threat-model-editor-heading">
              <h2 id="threat-model-editor-heading">
                <Plus aria-hidden="true" size={20} /> Draft next immutable revision
              </h2>
              {!canEdit ? (
                <p>Read-only. Only an owner or risk manager may append a reviewed revision.</p>
              ) : (
                <div className="threat-model-editor">
                  <p>
                    Expected parent: <code>{draft.expectedParentSha256 ?? "initial revision"}</code>
                    . Saving fails if another reviewer appends first.
                  </p>
                  <label className="form-field">
                    <span>Model title</span>
                    <input
                      value={draft.title}
                      onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    />
                  </label>

                  <fieldset className="threat-model-element-builder">
                    <legend>Add a graph-bound element</legend>
                    <label className="form-field">
                      <span>Element kind</span>
                      <select
                        value={newElement.kind}
                        onChange={(event) =>
                          setNewElement({
                            ...newElement,
                            kind: event.target.value as typeof newElement.kind,
                          })
                        }
                      >
                        <option value="asset">Asset</option>
                        <option value="actor">Actor</option>
                        <option value="entrypoint">Entrypoint</option>
                        <option value="trust_boundary">Trust boundary</option>
                        <option value="data_flow">Data flow</option>
                      </select>
                    </label>
                    <label className="form-field">
                      <span>Element title</span>
                      <input
                        value={newElement.title}
                        onChange={(event) =>
                          setNewElement({ ...newElement, title: event.target.value })
                        }
                      />
                    </label>
                    <label className="form-field">
                      <span>Description</span>
                      <textarea
                        value={newElement.description}
                        onChange={(event) =>
                          setNewElement({ ...newElement, description: event.target.value })
                        }
                      />
                    </label>
                    <fieldset className="threat-binding-options">
                      <legend>Evidence-observed nodes</legend>
                      {workspace.data.projection.graph.nodes.map((node) => (
                        <label key={node.id}>
                          <input
                            type="checkbox"
                            checked={newElement.nodeIds.includes(node.id)}
                            onChange={() =>
                              setNewElement({
                                ...newElement,
                                nodeIds: toggle(newElement.nodeIds, node.id),
                              })
                            }
                          />{" "}
                          <span>
                            {node.name} · {node.kind}
                          </span>
                        </label>
                      ))}
                    </fieldset>
                    <fieldset className="threat-binding-options">
                      <legend>Validated edges</legend>
                      {workspace.data.projection.graph.edges.length ? (
                        workspace.data.projection.graph.edges.map((edge) => (
                          <label key={edge.id}>
                            <input
                              type="checkbox"
                              checked={newElement.edgeIds.includes(edge.id)}
                              onChange={() =>
                                setNewElement({
                                  ...newElement,
                                  edgeIds: toggle(newElement.edgeIds, edge.id),
                                })
                              }
                            />{" "}
                            <span>
                              {edge.id} · {edge.fromNodeId} → {edge.toNodeId}
                            </span>
                          </label>
                        ))
                      ) : (
                        <p>No validated edges are available.</p>
                      )}
                    </fieldset>
                    {pendingError &&
                    (newElement.title ||
                      newElement.description ||
                      newElement.nodeIds.length ||
                      newElement.edgeIds.length) ? (
                      <p className="field-error">{pendingError}</p>
                    ) : null}
                    <button
                      className="button"
                      type="button"
                      disabled={Boolean(pendingError)}
                      onClick={addElement}
                    >
                      Add element to draft
                    </button>
                  </fieldset>

                  <h3>Draft elements ({draft.elements.length})</h3>
                  {draft.elements.length ? (
                    <ul className="threat-draft-elements">
                      {draft.elements.map((element) => (
                        <li key={element.id}>
                          <div>
                            <strong>{element.title}</strong>
                            <span>
                              {element.kind} · {element.nodeIds.length} node(s) ·{" "}
                              {element.edgeIds.length} edge(s)
                            </span>
                            <p>{element.description}</p>
                          </div>
                          <button
                            className="icon-button"
                            type="button"
                            aria-label={`Remove ${element.title} from draft`}
                            onClick={() =>
                              setDraft({
                                ...draft,
                                elements: draft.elements.filter((item) => item.id !== element.id),
                              })
                            }
                          >
                            <Trash2 aria-hidden="true" size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No elements in this draft. At least one graph-bound element is required.</p>
                  )}
                  <label className="form-field">
                    <span>Unresolved assumptions · one per line</span>
                    <textarea
                      value={draft.assumptionsText}
                      onChange={(event) =>
                        setDraft({ ...draft, assumptionsText: event.target.value })
                      }
                    />
                  </label>
                  <label className="form-field">
                    <span>Attributable change reason</span>
                    <textarea
                      value={changeReason}
                      onChange={(event) => setChangeReason(event.target.value)}
                    />
                  </label>
                  {draftErrors.length ? (
                    <p className="field-error">
                      Draft contains a stale or invalid graph binding. Refresh and review every
                      element.
                    </p>
                  ) : null}
                  {saveError ? <InlineError error={saveError} onRetry={() => void save()} /> : null}
                  <button
                    className="button button-primary"
                    type="button"
                    disabled={
                      saving ||
                      !draft.title.trim() ||
                      draft.elements.length === 0 ||
                      draftErrors.length > 0 ||
                      changeReason.trim().length < 10
                    }
                    onClick={() => void save()}
                  >
                    {saving ? "Appending…" : "Append immutable revision"}
                  </button>
                </div>
              )}
            </article>
          </details>

          <details className="governance-detail">
            <summary>Show immutable revision history</summary>
            <article className="panel-card">
              <h2>Revision history</h2>
              {workspace.data.revisions.length ? (
                <ol className="revision-list">
                  {[...workspace.data.revisions].reverse().map((revision) => (
                    <li key={revision.id}>
                      <strong>
                        Revision {revision.revision}: {revision.title}
                      </strong>
                      <span>
                        {revision.editorId} · {new Date(revision.createdAt).toLocaleString()}
                      </span>
                      <p>{revision.changeReason}</p>
                      <span>
                        {revision.elements.length} reviewed element(s) ·{" "}
                        {revision.assumptions.length} unresolved assumption(s)
                      </span>
                      <code>{shortHash(revision.canonicalSha256, 14)}</code>
                    </li>
                  ))}
                </ol>
              ) : (
                <p>No reviewed overlay has been appended yet.</p>
              )}
            </article>
          </details>
        </>
      ) : null}
    </section>
  );
}
